/**
 * kumo.device — 端末の機能（2026-09-26 モバイル Phase 1）。Web でも呼べる（できない事は静かに何もしない）。
 *
 *   kumo.device.platform            'web' | 'ios' | 'android'
 *   kumo.device.isNative            アプリ（Capacitor）の中か
 *   kumo.device.haptic('success')   触覚フィードバック。Web は Vibration API（対応端末だけ）
 *   kumo.device.openExternal(url)   OS のブラウザで開く（アプリ内の WebView から外へ出す）
 *   kumo.device.requestAgeRange()   年齢レンジ（iOS: Declared Age Range / Android: Play Age Signals）を取り、Kumo に記録
 *
 * WHY 触覚を SDK に置くか: App Review 4.2（最小機能）は「Web サイトを包んだだけ」のアプリを落とす。触覚はゲーム体験として
 * 自然で、実装が安く、「ネイティブ機能を使っている」証拠になる。例: 得点・失敗で鳴らす。
 *
 * WHY 年齢レンジを SDK に置くか: テキサス州 App Store Accountability Act（2026-06-04 施行）等の州法で、アプリはストアから
 * 渡される年齢区分を尊重する義務を負う。Kumo は「ストアの年齢シグナルを受け取ってプレイヤーに記録する口」を用意し、
 * 未成年向けの制限（チャット・課金・広告の出し分け）をサーバー側で判断できるようにする（判断ルールは未実装・founder 判断）。
 */

import { KumoError, type KumoCore } from './core.js';

export type HapticStyle = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection';

/** 公式 @capacitor/haptics（プラグイン名 'Haptics'）のうち使う部分 */
interface CapacitorHaptics {
  impact(o: { style: 'LIGHT' | 'MEDIUM' | 'HEAVY' }): Promise<void>;
  notification(o: { type: 'SUCCESS' | 'WARNING' | 'ERROR' }): Promise<void>;
  selectionChanged?(): Promise<void>;
}

/** @kumo/capacitor の KumoDevice（definitions.ts と一致させる） */
export interface DevicePlugin {
  haptic?(o: { style: HapticStyle }): Promise<void>;
  openExternal?(o: { url: string }): Promise<void>;
  /** 年齢レンジ。ageGates = 区切りの年齢（Apple の Declared Age Range は最大 3 つ） */
  requestAgeRange?(o: { ageGates: number[] }): Promise<AgeRangeSignal>;
  /** iOS: StoreKit の Storefront.current?.countryCode（例 'USA'）。Android は null */
  storefront?(): Promise<{ countryCode: string | null }>;
}

export interface AgeRangeSignal {
  /** shared = 本人（または保護者）が共有した / declined = 共有を断った / unavailable = OS・地域が未対応 */
  status: 'shared' | 'declined' | 'unavailable';
  lowerBound?: number | null;
  upperBound?: number | null;
  /** どの仕組みから来たか */
  source?: 'apple_declared_age_range' | 'google_play_age_signals' | null;
  /** 保護者の管理下にあるか（取れる OS だけ） */
  supervised?: boolean | null;
}

/** Web のバイブレーションの長さ（ms）。触覚の強さを長さで近似する */
const VIBRATE_MS: Record<HapticStyle, number | number[]> = {
  light: 10,
  medium: 20,
  heavy: 35,
  selection: 5,
  success: [15, 40, 15],
  warning: [25, 60, 25],
  error: [40, 60, 40]
};

export function createDevice(core: KumoCore) {
  const bridge = core.native;
  const haptics = () => bridge?.plugin<CapacitorHaptics>('Haptics') ?? null;
  const device = () => bridge?.plugin<DevicePlugin>('KumoDevice') ?? null;

  return {
    get platform() {
      return core.platform;
    },
    get isNative() {
      return core.platform !== 'web';
    },

    /** 触覚。失敗（未対応端末・権限）は握りつぶす = ゲームの処理を止めない */
    async haptic(style: HapticStyle = 'light'): Promise<void> {
      try {
        const h = haptics();
        if (h) {
          if (style === 'success' || style === 'warning' || style === 'error') return await h.notification({ type: style.toUpperCase() as 'SUCCESS' });
          if (style === 'selection' && h.selectionChanged) return await h.selectionChanged();
          return await h.impact({ style: (style === 'selection' ? 'LIGHT' : style.toUpperCase()) as 'LIGHT' });
        }
        const d = device();
        if (d?.haptic) return await d.haptic({ style });
        const nav = (globalThis as { navigator?: { vibrate?: (p: number | number[]) => boolean } }).navigator;
        nav?.vibrate?.(VIBRATE_MS[style]);
      } catch {
        /* 触覚は演出。失敗してもゲームは続ける */
      }
    },

    /** OS のブラウザで開く（アプリでは WebView の中で開かない = App Store の「外部リンク」の扱いと一致させる） */
    async openExternal(url: string): Promise<void> {
      const d = device();
      if (d?.openExternal) return d.openExternal({ url });
      const w = globalThis as { open?: (u: string, t: string, f?: string) => unknown };
      w.open?.(url, '_blank', 'noopener');
    },

    /**
     * 年齢レンジを OS から取り、サーバーに記録する（`POST /v1/players/me/age-signal`・サーバーは players/appRoutes.ts）。
     * Web では OS の仕組みが無いので { status: 'unavailable' } を返す（自己申告の年齢確認は別途ゲーム側で）。
     */
    async requestAgeRange(opts: { ageGates?: number[] } = {}): Promise<AgeRangeSignal> {
      const d = device();
      if (!bridge || !d?.requestAgeRange) return { status: 'unavailable' };
      const signal = await d.requestAgeRange({ ageGates: opts.ageGates ?? [13, 16, 18] });
      if (!core.isSignedIn) throw new KumoError(401, 'signed_out', 'Sign in (or start as a guest) before recording an age signal');
      await core.request('POST', '/v1/players/me/age-signal', { ...signal, platform: core.platform });
      return signal;
    },

    /** iOS のストアフロント（'USA' 等）。米国だけ Web 決済へのリンクを出せる判定に使う（store.ts）。取れなければ null */
    async storefront(): Promise<string | null> {
      const d = device();
      if (!d?.storefront) return null;
      try {
        return (await d.storefront()).countryCode ?? null;
      } catch {
        return null;
      }
    }
  };
}

export type KumoDevice = ReturnType<typeof createDevice>;
